// src/components/CreditNoteForm.js
// Issue a Credit Note against a specific student invoice (fee waiver / discount /
// correction). Reduces that invoice's outstanding and posts Dr Revenue / Cr AR.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Autocomplete, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, FormHelperText, Alert, Divider,
  IconButton, Tooltip, Table, TableHead, TableBody, TableRow, TableCell
} from '@mui/material';
import { Home, ArrowBack } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const outstandingOf = (inv) => inv ? inv.amount - (inv.paid || 0) - (inv.credited || 0) : 0;

const CreditNoteForm = () => {
  const navigate = useNavigate();
  const [learners, setLearners]         = useState([]);
  const [learnerAdmNo, setLearnerAdmNo] = useState('');
  const [invoices, setInvoices]         = useState([]);
  const [invLoading, setInvLoading]     = useState(false);
  const [selectedInvoiceNo, setInvoice] = useState('');
  const [amount, setAmount]             = useState('');
  const [reason, setReason]             = useState('');
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');
  const [notes, setNotes]               = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/academics/learners`, { headers: hdrs() })
      .then(r => setLearners(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const loadNotes = useCallback((admNo) => {
    if (!admNo) { setNotes([]); return; }
    axios.get(`${API}/api/finance/credit-notes/${admNo}`, { headers: hdrs() })
      .then(r => setNotes(Array.isArray(r.data) ? r.data : []))
      .catch(() => setNotes([]));
  }, []);

  const loadInvoices = useCallback((admNo) => {
    if (!admNo) { setInvoices([]); return; }
    setInvLoading(true);
    axios.get(`${API}/api/finance/invoices/unpaid/${admNo}`, { headers: hdrs() })
      .then(r => setInvoices(Array.isArray(r.data) ? r.data : []))
      .catch(() => setInvoices([]))
      .finally(() => setInvLoading(false));
  }, []);

  const reverseNote = async (id) => {
    setError(''); setSuccess('');
    try {
      await axios.post(`${API}/api/finance/credit-notes/${id}/reverse`, {}, { headers: hdrs() });
      setSuccess('Credit note reversed.');
      loadNotes(learnerAdmNo); loadInvoices(learnerAdmNo);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reverse credit note');
    }
  };

  const selectedInvoice = invoices.find(i => i.invoiceNo === selectedInvoiceNo);
  const outstanding = outstandingOf(selectedInvoice);

  const handleSubmit = async () => {
    if (submitting) return;
    if (!learnerAdmNo || !selectedInvoiceNo || !amount || !reason.trim()) {
      setError('Learner, invoice, amount and reason are all required'); return;
    }
    if (parseFloat(amount) > outstanding + 0.001) {
      setError(`Credit cannot exceed outstanding (${sym()} ${fmt(outstanding)})`); return;
    }
    setSubmitting(true); setError(''); setSuccess('');
    try {
      const { data } = await axios.post(`${API}/api/finance/credit-notes`, {
        learnerAdmNo, invoiceNo: selectedInvoiceNo, amount: parseFloat(amount), reason: reason.trim(),
      }, { headers: hdrs() });
      setSuccess(`Credit note ${data.creditNote?.creditNoteNo || ''} posted.`);
      setInvoice(''); setAmount(''); setReason('');
      loadInvoices(learnerAdmNo); loadNotes(learnerAdmNo); // refresh outstanding + list
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to post credit note');
    } finally { setSubmitting(false); }
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4, mb: 6 }}>
      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h5" color="primary" fontWeight="bold">Credit Note</Typography>
            <Box display="flex" gap={1}>
              <Button startIcon={<ArrowBack />} size="small" variant="outlined" onClick={() => navigate(-1)}>Back</Button>
              <Tooltip title="Finance Home">
                <IconButton onClick={() => navigate('/finance')} sx={{ bgcolor: 'background.paper' }}><Home /></IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Reduce what a learner owes on a specific invoice — a waiver, discount, or billing correction.
            This is not a cash refund and does not delete the invoice.
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={learners}
                getOptionLabel={(o) => (typeof o === 'string' ? o : `${o.name} — ${o.admNo}`)}
                onChange={(_, v) => {
                  const admNo = v && typeof v === 'object' ? v.admNo : '';
                  setLearnerAdmNo(admNo); setInvoice(''); setAmount(''); loadInvoices(admNo); loadNotes(admNo);
                }}
                renderInput={(params) => <TextField {...params} label="Learner (name or adm no)" placeholder="Type to search…" />}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required disabled={!learnerAdmNo || invLoading}>
                <InputLabel>Invoice to credit</InputLabel>
                <Select
                  value={selectedInvoiceNo}
                  label="Invoice to credit"
                  onChange={e => {
                    const inv = invoices.find(i => i.invoiceNo === e.target.value);
                    setInvoice(e.target.value);
                    setAmount(inv ? outstandingOf(inv).toFixed(2) : '');
                  }}
                >
                  {invLoading ? <MenuItem disabled>Loading…</MenuItem>
                    : invoices.length === 0 ? <MenuItem disabled>No open invoices</MenuItem>
                    : invoices.map(inv => (
                      <MenuItem key={inv.invoiceNo} value={inv.invoiceNo}>
                        {inv.invoiceNo} – {inv.description} (Outstanding: {sym()} {fmt(outstandingOf(inv))})
                      </MenuItem>
                    ))}
                </Select>
                <FormHelperText>{learnerAdmNo ? 'Pick the invoice to adjust' : 'Select a learner first'}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Credit Amount" type="number" value={amount} fullWidth required
                onChange={e => setAmount(e.target.value)}
                inputProps={{ step: '0.01', min: '0.01' }}
                helperText={selectedInvoice
                  ? <span>Outstanding: <strong>{sym()} {fmt(outstanding)}</strong>{parseFloat(amount) > outstanding && <span style={{ color: 'red' }}> (exceeds!)</span>}</span>
                  : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Reason" value={reason} fullWidth required
                onChange={e => setReason(e.target.value)}
                placeholder="e.g., Sibling discount, billing error"
              />
            </Grid>

            <Grid item xs={12} sx={{ textAlign: 'center', mt: 1 }}>
              <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={{ mr: 2 }}>
                {submitting ? 'Posting…' : 'Post Credit Note'}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/finance')} disabled={submitting}>Cancel</Button>
            </Grid>
          </Grid>

          {learnerAdmNo && notes.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>Posted Credit Notes</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Ref</strong></TableCell>
                    <TableCell><strong>Invoice</strong></TableCell>
                    <TableCell><strong>Reason</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell align="center"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notes.map(n => (
                    <TableRow key={n._id} hover>
                      <TableCell>{n.creditNoteNo}</TableCell>
                      <TableCell>{n.invoiceNo}</TableCell>
                      <TableCell>{n.reason}</TableCell>
                      <TableCell align="right">{sym()} {fmt(n.amount)}</TableCell>
                      <TableCell align="center">
                        <Button size="small" color="error" variant="outlined"
                          onClick={() => reverseNote(n._id)}>Reverse</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Reversing un-credits the invoice and posts a reversing GL entry (admin only).
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreditNoteForm;
